import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X, ChevronDown, User, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth } from "../contexts/AuthContext";
import AuthModal from "./AuthModal";
import LanguageSwitcher from "./LanguageSwitcher";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [aiServices, setAiServices] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout, loading } = useAuth();
  
  // Check if we're on home page - only home page has dark hero background
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchAiServices = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${backendUrl}/api/wcd-programs/`);
        if (response.ok) {
          const services = await response.json();
          setAiServices(services);
        }
      } catch (error) {
        console.error('Error fetching AI services:', error);
      }
    };
    
    fetchAiServices();
  }, []);

  const navItems = [
    { label: "Home", href: "#home" },
    { 
      label: "WCD Initiatives", 
      href: "#ai-services",
      dropdown: aiServices.map(service => ({
        label: service.title,
        href: `/wcd-programs/${service.slug}`
      }))
    },
    { 
      label: "Dashboards",
      dropdown: [
        { label: "Field Monitoring & AVT", href: "/dashboards/avt" },
        { label: "THR Distribution & Leakages", href: "/dashboards/thr" },
        { label: "Aadhaar & FRS Authentication", href: "/dashboards/frs" },
        { label: "Hot Cooked Meal & Fortified Rice", href: "/dashboards/hcm" },
        { label: "Growth Monitoring Quality", href: "/dashboards/growth-monitoring" },
        { label: "Enrollment & Drop-off Management", href: "/dashboards/enrollment-dropout" },
        { label: "AWC Infrastructure Strengthening", href: "/dashboards/infrastructure" },
        { label: "PURNA & Poshan Sangam", href: "/dashboards/purna" }
      ]
    },
    { label: "Objective", href: "/objective" },
    { label: "Gujarat Map", href: "#map" },
    { label: "Schemes & News", href: "#news" },
    { label: "Partners", href: "#partnerships" }
  ];

  const handleNavClick = (href) => {
    if (href.startsWith('/')) {
      // Navigate to different page
      navigate(href);
    } else if (href.startsWith('#')) {
      // Scroll to section on current page
      if (location.pathname !== '/') {
        // If not on home page, navigate to home first then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        (isHomePage && !isScrolled)
          ? "bg-transparent" 
          : "bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200/20"
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-20 gap-2 sm:gap-4 lg:gap-6">
          {/* Logo */}
          <div className="flex items-center space-x-2 cursor-pointer flex-shrink-0" onClick={handleLogoClick}>
            <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">G</span>
            </div>
            <div className="hidden sm:block">
              <h1 className={`text-sm sm:text-base xl:text-lg font-bold leading-tight transition-colors ${(isHomePage && !isScrolled) ? 'text-white' : 'text-gray-900'}`}>Gujarat WCD</h1>
              <p className={`text-[10px] sm:text-xs leading-tight whitespace-nowrap transition-colors ${(isHomePage && !isScrolled) ? 'text-white/90' : 'text-gray-600'}`}>Women & Child Development</p>
            </div>
          </div>

          {/* Desktop Navigation - Full width with proper spacing */}
          <nav className="hidden lg:flex items-center flex-1 min-w-0 justify-center gap-0.5 xl:gap-1 2xl:gap-2 px-1 lg:px-2 overflow-hidden">
            {navItems.map((item) => (
              item.dropdown ? (
                <DropdownMenu key={item.label}>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className={`${(isHomePage && !isScrolled) ? 'text-white' : 'text-gray-900'} hover:text-pink-600 transition-colors flex items-center gap-0.5 whitespace-nowrap px-1.5 xl:px-2 py-2 text-[11px] xl:text-xs 2xl:text-sm font-medium`}
                    >
                      <span className="truncate">{item.label}</span>
                      <ChevronDown className="w-3 h-3 xl:w-3.5 xl:h-3.5 flex-shrink-0" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    {item.dropdown.map((dropdownItem) => (
                      <DropdownMenuItem 
                        key={dropdownItem.label}
                        onClick={() => handleNavClick(dropdownItem.href)}
                        className="cursor-pointer hover:text-pink-600"
                      >
                        {dropdownItem.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item.href)}
                  className={`${(isHomePage && !isScrolled) ? 'text-white' : 'text-gray-900'} hover:text-pink-600 transition-colors font-medium whitespace-nowrap px-1.5 xl:px-2 py-2 text-[11px] xl:text-xs 2xl:text-sm`}
                >
                  {item.label}
                </button>
              )
            ))}
          </nav>

          {/* Auth & CTA Buttons & Language */}
          <div className="hidden lg:flex items-center gap-1 xl:gap-2 flex-shrink-0 ml-auto">
            {isAuthenticated && user ? (
              <>
                <Button 
                  className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-medium px-2 xl:px-3 py-2 text-[11px] xl:text-xs transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                  onClick={() => handleNavClick("#dashboard")}
                >
                  View Dashboard
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 px-3 py-2 whitespace-nowrap">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-5 h-5 rounded-full" />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                      <span className="text-sm truncate max-w-[100px]">{user.name}</span>
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem className="font-medium">
                      {user.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      {user.district && (
                        <span className="text-xs text-gray-500 block">{user.district}</span>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={logout}
                      className="cursor-pointer text-red-600"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button 
                  className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-medium px-2 xl:px-3 py-2 text-[11px] xl:text-xs transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                  onClick={() => handleNavClick("#dashboard")}
                >
                  View Dashboard
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setAuthModalOpen(true)}
                  className="border-pink-200 hover:bg-pink-50 hover:border-pink-300 px-2 xl:px-3 py-2 text-[11px] xl:text-xs whitespace-nowrap"
                >
                  Sign In
                </Button>
              </>
            )}
            <LanguageSwitcher />
          </div>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-gray-200/20">
            <div className="px-4 py-4 space-y-3">
              {navItems.map((item) => (
                <div key={item.label}>
                  <button
                    onClick={() => handleNavClick(item.href)}
                    className="block w-full text-left text-gray-700 hover:text-pink-600 transition-colors font-medium py-2"
                  >
                    {item.label}
                  </button>
                  {item.dropdown && (
                    <div className="ml-4 mt-2 space-y-2">
                      {item.dropdown.map((dropdownItem) => (
                        <button
                          key={dropdownItem.label}
                          onClick={() => handleNavClick(dropdownItem.href)}
                          className="block w-full text-left text-sm text-gray-600 hover:text-pink-600 transition-colors py-1"
                        >
                          {dropdownItem.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t border-gray-200 space-y-3">
                {isAuthenticated && user ? (
                  <>
                    <div className="flex items-center gap-3 p-3 bg-pink-50 rounded-lg">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-6 h-6 text-pink-600" />
                      )}
                      <div>
                        <p className="font-medium text-sm">{user.name}</p>
                        <p className="text-xs text-gray-500">
                          {user.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                      </div>
                    </div>
                    <Button 
                      className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
                      onClick={() => handleNavClick("#dashboard")}
                    >
                      View Dashboard
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full border-red-200 text-red-600 hover:bg-red-50"
                      onClick={logout}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
                      onClick={() => handleNavClick("#dashboard")}
                    >
                      View Dashboard
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full border-pink-200 hover:bg-pink-50"
                      onClick={() => setAuthModalOpen(true)}
                    >
                      Sign In
                    </Button>
                  </>
                )}
                
                {/* Language Switcher for Mobile */}
                <div className="pt-4 border-t border-gray-200">
                  <LanguageSwitcher className="w-full justify-center" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode="login"
      />
    </header>
  );
};

export default Header;
